import { z } from "zod";

import type { ConnectorIconVariant } from "./artifacts-shared.ts";
import type { DerivedThreadStatus } from "./threadCardStatus.ts";

/**
 * Wire types for the user's task list (`GET /api/a/user-tasks`) — the list the
 * product calls "what's on your plate". Separate from `types/tasks.ts`, which
 * serves the older per-thread task records.
 */

/** Rollout gate for the task list, read by the API and the web app alike. Off by default. */
export const TASKS_V2_GATE = "tasks-v2";

/**
 * The simpler Tasks page: three categories, title-only rows, a few fixed
 * actions. Read only by the clients, and only under `tasks-v2`.
 */
export const TASKS_SIMPLE_LIST_GATE = "tasks-simple-list";

/**
 * Detection has its own gate beside the list's, so it can be switched off
 * without hiding the list — a run of wrong tasks is stopped at the source. It
 * covers every source read with a model: mail, conversations with okthx, and
 * meeting notes.
 */
export const TASKS_DETECTION_GATE = "tasks-v2-detection";

/**
 * A brand mark: the service a record came through, plus which of its products
 * this is. Resolved on the server so the rule lives once.
 *
 * `provider` is a vendor name for mail and calendars and the connector
 * catalog's own slug for a saved service object, which is the vocabulary both
 * clients' icon maps are keyed by. Not every value is in both maps, and
 * neither map is exhaustive over the connector catalog, so a client resolves
 * it to a mark where it has one and falls back where it does not.
 */
export type SourceProviderIcon = { provider: string; variant: ConnectorIconVariant };

export type UserTaskSource = {
  urn: string;
  /** "source" for mail, meetings and pulled documents; "thread" for a conversation with okthx. */
  type: string;
  title: string | null;
  /** What this source did to the task, oldest first. Empty for a conversation, which keeps none. */
  roles?: { role: string; atMs: number; reason?: string }[];
  /**
   * For a text: okthx's thread for its conversation, where every message is,
   * which the link opens; null when okthx has none (BIG-8841). Absent for
   * anything else, and from an older server.
   */
  threadId?: number | null;
  /**
   * The brand mark the row draws for where this came from. Null where nothing
   * in the record names a service — a conversation with okthx, a text, a
   * connector the account has dropped — and absent only from an older server.
   */
  providerIcon?: SourceProviderIcon | null;
};

export type UserTask = {
  id: string;
  urn: string;
  title: string;
  /** "suggested" | "open" | "later" | "done" | "cancelled" */
  status: string;
  /** Whose task it is: "user" | "okthx". */
  assignee: string;
  closedReason: string | null;
  closedAtMs: number | null;
  snoozedUntilMs: number | null;
  /**
   * okthx's check-in on a quiet task, in its own words: "stale" — the user's
   * own task, untouched — or "silence" — a task that waits on someone who has
   * said nothing.
   */
  checkIn: UserTaskCheckIn | null;
  /** Who messages about it go out as, once the user has said: "user" | "okthx". */
  sendAs?: string | null;
  /** The person it waits for, whoever owns it. `name` is null when no name is known. */
  waitingOn: { name: string | null } | null;
  /**
   * When the user said they were working on it themselves: in progress, with
   * nothing awaited from anyone else. Null when it is not held.
   */
  heldAtMs?: number | null;
  /**
   * When the user committed to doing it by the day it is snoozed to, as
   * opposed to putting it off: a promise rather than a postponement. Null
   * when the snooze is a plain one, and once the task is back.
   */
  committedAtMs?: number | null;
  /** okthx is working on it, and its thread is waiting on the user. */
  needsInput?: boolean;
  /** Where it stands now. */
  context: UserTaskContext | null;
  /** The evidence a close rested on, when a source closed it: "Why was this closed?". */
  closedBy: { reason: string; source: UserTaskSource | null; byOkthx?: boolean } | null;
  /** The labels it is under, and projects among them. */
  labels?: { id: string; name: string }[];
  /** Who asked, when someone other than the user did. */
  askedBy: string | null;
  /** The asker's name, when known. */
  askedByName: string | null;
  /** What was asked or promised, in a sentence. Sent for suggested tasks only. */
  description: string | null;
  /** A task detection thought this might duplicate; Triage offers "Same as …?". */
  possibleDuplicateOf: { id: string; title: string | null } | null;
  /**
   * A suggestion the user dismissed in Triage. Kept as the record of the
   * answer; the Tasks tab leaves it out of Closed. Optional while an older
   * server, which does not send it, is still serving.
   */
  dismissed?: boolean;
  /**
   * Closed by a Dismiss that Undo can put back: the dismissal saved what it
   * closed. A suggestion a mute dismissed is closed without it. Optional while
   * an older server is serving.
   */
  undoable?: boolean;
  dueMs: number | null;
  /**
   * The day it is due, `YYYY-MM-DD`, when it is due on a day rather than at a
   * time — an imported list gives a date only, kept as midnight in the
   * account's timezone. The day is the account's, so a browser in another
   * timezone shows the same date. Optional while an older server is serving.
   */
  dueDate?: string;
  /** The thread okthx works on it in, when okthx has taken it on there. */
  threadId?: number | null;
  /**
   * Where that thread stands, as its home card shows it, so View thread and
   * the card agree. Null when there is no thread, or it was deleted; absent
   * from an older server. "waiting" names no one: the person is `waitingOn`.
   */
  threadStatus?: DerivedThreadStatus | null;
  /** The user's own task tool it is mirrored to: "todoist". */
  mirroredTo?: string | null;
  /** When detection or the user first put it on the list, on the caller's clock. */
  createdAtMs: number;
  /**
   * When something its row shows last changed: created, status, title, due
   * date, snooze date, thread link or closed reason. The simpler list sorts by
   * it. Optional while an older server, which does not send it, is serving.
   */
  lastActivityMs?: number;
  parentId: string | null;
  sources: UserTaskSource[];
};

/** How recently detection read one kind of source, and how that went. */
export type UserTaskCoverage = {
  /** "mail", "pulled", or a puller's service — "granola". */
  source: string;
  /** "read" | "truncated" (a first run that may have left older mail unread) | "empty" | "not-ready" | "not-connected" | "failed" */
  status: string;
  atMs: number;
};

/** One of the user's labels. A label with a goal is a project. */
export type UserTaskLabel = {
  id: string;
  name: string;
  goal: string | null;
  dueMs: number | null;
  /** "open", or "done" once its goal is met. */
  status: string;
  /** Why it closed, when it is done. */
  closedReason: string | null;
};

/** A label okthx proposes, waiting in Suggested for Keep or Dismiss. */
export type UserTaskLabelProposal = {
  id: string;
  name: string;
  tasks: { id: string; title: string }[];
};

export type UserTasksResp = {
  tasks: UserTask[];
  /** What detection has read, so the list can say what it has not. */
  coverage?: UserTaskCoverage[];
  /** The user's open labels. */
  labels?: UserTaskLabel[];
  proposals?: UserTaskLabelProposal[];
};

/** The simpler list's categories (`tasks-simple-list`). Suggested is a group inside Open. */
export const simpleTaskCategories = ["open", "snoozed", "closed"] as const;
export type SimpleTaskCategory = (typeof simpleTaskCategories)[number];

export const simpleTasksQuery = z.object({
  category: z.enum(simpleTaskCategories).default("open"),
});

/** One group of the simpler list, already sorted. */
export type SimpleTaskGroup = {
  key: "suggested" | "open" | "snoozed" | "closed";
  tasks: UserTask[];
};

/**
 * `GET /api/a/user-tasks/simple`: one category's groups, in order, empty ones
 * left out.
 */
export type SimpleTasksResp = {
  groups: SimpleTaskGroup[];
};

/** A label's own page: everything under it, and okthx's reading of it against its goal. */
export type UserTaskProject = {
  label: UserTaskLabel;
  /** Where the label stands: okthx's reading in words, or the user's correction. */
  context: UserTaskContext | null;
  /** okthx's current reading against the goal, when it is up to date. */
  reading: {
    /** Absent for a label without a goal. */
    goalMet: boolean | null;
    /** Parts of the goal no task covers. */
    missing: string[];
  } | null;
  /**
   * okthx tried to read the label and could not. The page still has everything
   * else, but `reading` is absent because it was not taken rather than because
   * there was nothing to say — and an empty `missing` must not be read as
   * "every part of the goal is covered".
   */
  readingFailed?: boolean;
  tasks: UserTask[];
  threads: { id: number; title: string }[];
  artifacts: { id: string; type: string; typeLabel: string; title: string }[];
};

const entIdParam = z.string().regex(/^\d+$/);

export const userTaskLabelIdParam = z.object({ labelId: entIdParam });

export const answerLabelProposalReq = z.object({ answer: z.enum(["keep", "dismiss"]) });
export type AnswerLabelProposalReq = z.infer<typeof answerLabelProposalReq>;

/** Add a missing part of the goal as a task under the tag. */
export const addProjectTaskReq = z.object({ title: z.string().trim().min(1).max(200) });
export type AddProjectTaskReq = z.infer<typeof addProjectTaskReq>;

export const userTaskIdParam = z.object({ taskId: entIdParam });

/**
 * An address, or a domain with a name and a suffix: a bare suffix such as
 * "com" would mute every sender under it.
 */
const MUTE_VALUE = /^@?(?:[^\s@]+@)?[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

/** Hand to okthx, or Ask about this: the conversation the task is now talked about in. */
export type TaskThreadResp = { threadId: number };

/** A sender to mute for task detection: an address, or a bare domain ("micro.so"). */
export const muteSenderReq = z.object({
  value: z.string().trim().min(3).max(320).regex(MUTE_VALUE),
});
export type MuteSenderReq = z.infer<typeof muteSenderReq>;

/** What a mute dropped: the open tasks it cancelled and the suggestions it dismissed. */
export type MuteSenderResp = { cancelledTaskIds: string[] };

/** A sender or domain the user muted. */
export type UserTaskMutedSender = { value: string; atMs: number };
export type UserTaskMutedSendersResp = {
  mutedSenders: UserTaskMutedSender[];
  /** The user's own mail domains, which a mute may not cover. */
  ownDomains: string[];
};

/** A change that answers with nothing to say. */
export type OkResp = { ok: true };

/** The model's draft of a rule, for the user to confirm or edit. */
export type DraftTaskRuleResp = { text: string };

/** A rule the user gave about what is a task. */
export type UserTaskRule = { id: string; text: string; createdAtMs: number; fromTaskId?: string };
export type UserTaskRulesResp = { rules: UserTaskRule[] };
/** The rule just saved. */
export type AddTaskRuleResp = { rule: UserTaskRule };

/** A rule the user gives about what is a task, from the task it came from when there is one. */
export const addTaskRuleReq = z.object({
  // Saved without < and >, so it must still have words once they are out.
  text: z
    .string()
    .trim()
    .min(3)
    .max(300)
    .refine((text) => text.replace(/[<>]/g, "").trim().length > 0),
  fromTaskId: entIdParam.optional(),
});
export type AddTaskRuleReq = z.infer<typeof addTaskRuleReq>;

/** Draft a rule from a task: the user's own words about it, when they gave any. */
export const draftTaskRuleReq = z.object({ words: z.string().trim().max(500).optional() });
export type DraftTaskRuleReq = z.infer<typeof draftTaskRuleReq>;

export const taskRuleIdParam = z.object({ ruleId: z.string().uuid() });

/**
 * Hand to okthx, or Keep and hand off: the user's own words for okthx, when
 * they add any. An older web sends no body fields.
 */
export const handoffReq = z.object({ note: z.string().trim().max(2000).optional() });
export type HandoffReq = z.infer<typeof handoffReq>;

/** Undo a mute, reopening the tasks the mute dropped. */
export const unmuteSenderReq = muteSenderReq.extend({
  // A domain mute can drop thousands; Undo sends every one it dropped.
  reopenTaskIds: z.array(entIdParam).max(5000).optional(),
});
export type UnmuteSenderReq = z.infer<typeof unmuteSenderReq>;

/**
 * Why the user dismissed a suggestion or dropped a task (BIG-8844). Each
 * teaches detection something different: already done, it was a real task;
 * not important, a weak ask; not a task, not one at all.
 *
 * `other` is the catch-all, and is always stored with `reasonText` — the
 * user's own words. It is a real value rather than an absent reason, because
 * an absent one means something else entirely: an answer given before we
 * asked. One says the reader had something to say; the other says nobody
 * asked them.
 *
 * Add freely; never reuse a name. These values are the training signal, and a
 * repurposed name silently rewrites what every stored answer meant.
 */
export const dismissReasons = ["alreadyDone", "notImportant", "notATask", "other"] as const;
export type DismissReason = (typeof dismissReasons)[number];

/**
 * How long the user's own words may be. Short on purpose: a sentence or two
 * is what a dialog in Triage can ask for, and it bounds a free-text field
 * before anything downstream reads it.
 */
export const MAX_DISMISS_REASON_CHARS = 280;

/**
 * The reader's own words, for `other`. Saved without < and >, as a rule's
 * text is, so it must still have words once they are out.
 */
const reasonTextField = z
  .string()
  .trim()
  .min(1)
  .max(MAX_DISMISS_REASON_CHARS)
  .refine((text) => text.replace(/[<>]/g, "").trim().length > 0);

/**
 * The words and the reason travel together: `other` is the one reason that
 * has them, and the only place they mean anything. Refused at the wire so a
 * client that sends one without the other gets a 400 it can act on, rather
 * than a 200 that quietly threw the words away.
 */
function wordsOnlyForOther(
  req: { reasonText?: string },
  forOther: boolean,
  ctx: z.RefinementCtx,
): void {
  if (forOther && req.reasonText === undefined) {
    ctx.addIssue({
      code: "custom",
      path: ["reasonText"],
      message: "reasonText is required when the reason is other",
    });
  }
  if (!forOther && req.reasonText !== undefined) {
    ctx.addIssue({
      code: "custom",
      path: ["reasonText"],
      message: "reasonText is only for a reason of other",
    });
  }
}

/**
 * Why the user dropped an open task: "already done" is the Done button.
 *
 * `other` carries the reader's own words in `reasonText`, as a dismissal does.
 * `notImportant` stays accepted for the clients that still send it; rejecting
 * it would turn their drops into 400s. New clients do not offer it.
 */
export const dropReasons = ["notImportant", "notATask", "other"] as const;
export type DropReason = (typeof dropReasons)[number];

/** The user's answer to a suggested task. */
export const answerTriageReq = z
  .object({
    answer: z.enum(["keep", "dismiss", "sameAs"]),
    /** Required for "sameAs": the task this one duplicates. */
    sameAsTaskId: entIdParam.optional(),
    /** For "dismiss": why. Optional while an older web is serving. */
    reason: z.enum(dismissReasons).optional(),
    reasonText: reasonTextField.optional(),
  })
  .superRefine((req, ctx) =>
    wordsOnlyForOther(req, req.answer === "dismiss" && req.reason === "other", ctx),
  );
export type AnswerTriageReq = z.infer<typeof answerTriageReq>;

/**
 * Why, given after a Dismiss that already happened (BIG-9631): one of the
 * dismiss reasons, or Same as… with the task a suggestion duplicates.
 */
export const dismissalReasonReq = z.union([
  z.strictObject({ reason: z.literal("sameAs"), sameAsTaskId: entIdParam }),
  z
    .strictObject({ reason: z.enum(dismissReasons), reasonText: reasonTextField.optional() })
    .superRefine((req, ctx) => wordsOnlyForOther(req, req.reason === "other", ctx)),
]);
export type DismissalReasonReq = z.infer<typeof dismissalReasonReq>;

/** Where a task or a label stands now. `by` is "okthx" or "user": the user's text okthx keeps. */
export type UserTaskContext = {
  text: string;
  by: string;
  atMs: number;
  /** The user's latest correction, when okthx has written the text since. */
  userText?: string | null;
};

export type UserTaskCheckIn = { kind: "stale" | "silence"; text: string; atMs: number };

/** The user's correction to a task's or a label's context. */
export const setContextReq = z.object({ text: z.string().trim().min(1).max(4000) });
export type SetContextReq = z.infer<typeof setContextReq>;

/** A task still on the list: open, or snoozed until later. */
export function isActiveStatus(status: string): boolean {
  return status === "open" || status === "later";
}

/** A finished task: done, or cancelled. The status says how it ended; `closedReason` says why. */
export function isClosedStatus(status: string): boolean {
  return status === "done" || status === "cancelled";
}

/** Done, Drop, Later or Reopen, from a row's actions. */
export const setUserTaskStatusReq = z
  .object({
    status: z.enum(["open", "later", "done", "cancelled"]),
    /** Why it ended, when there is more to say than the status. */
    closedReason: z.string().min(1).max(500).optional(),
    /** For a Drop: why, which detection learns from. Optional while an older web is serving. */
    reason: z.enum(dropReasons).optional(),
    reasonText: reasonTextField.optional(),
    /** When a Later task comes back. Required for Later: without it the task never would. */
    snoozedUntilMs: z.number().int().positive().optional(),
    /**
     * The Later is a commitment: the user said they will do it by the day it
     * comes back on, rather than putting it off. The day becomes the task's
     * due date, so a missed one is late and not merely back.
     */
    committed: z.boolean().optional(),
    /**
     * The status the page showed when the user tapped: a task that changed
     * since — okthx closed it, another tab moved it — is a 409, not a move
     * over a change the user never saw. Optional while an older web is serving.
     */
    fromStatus: z.enum(["open", "later", "done", "cancelled"]).optional(),
    /**
     * `false`: a reopen changes the status alone, without asking okthx to
     * resume work in the task's thread. Absent means the old behavior.
     */
    resumeWork: z.boolean().optional(),
  })
  .refine((req) => req.status !== "later" || req.snoozedUntilMs !== undefined, {
    message: "snoozedUntilMs is required for later",
    path: ["snoozedUntilMs"],
  })
  .refine((req) => !req.committed || req.status === "later", {
    message: "only a later can be a commitment",
    path: ["committed"],
  })
  .superRefine((req, ctx) =>
    wordsOnlyForOther(req, req.status === "cancelled" && req.reason === "other", ctx),
  );
export type SetUserTaskStatusReq = z.infer<typeof setUserTaskStatusReq>;

/** The user's answer to "Nothing from Sam about it yet. Have you heard anything?". */
export const answerSilenceReq = z.object({
  answer: z.enum(["gotIt", "stillWaiting"]),
});
export type AnswerSilenceReq = z.infer<typeof answerSilenceReq>;

/** The user's own task tool, as the Tasks tab shows its switch. */
export type UserTaskMirror = {
  service: string;
  /** "Todoist" */
  label: string;
  /** The account has the service connected; the switch shows only then. */
  connected: boolean;
  /** New tasks go to this tool. At most one tool is the default. */
  isDefault: boolean;
  /** When okthx last read the tool back. */
  lastCheckedAtMs: number | null;
  /** How the last call to the tool went: "ok" | "not-connected" | "failed". */
  lastStatus: string | null;
};

export type UserTaskMirrorResp = { mirrors: UserTaskMirror[] };

export const userTaskMirrorServiceParam = z.object({ service: z.string().regex(/^[a-z0-9-]+$/) });

export const setUserTaskMirrorReq = z.object({ isDefault: z.boolean() });
export type SetUserTaskMirrorReq = z.infer<typeof setUserTaskMirrorReq>;

/**
 * How one sync with a tool went. "off" when the account has not turned the
 * tool on, or it is not the default and no task is linked to it. "busy" when
 * another sync holds it: it runs again shortly.
 */
export type UserTaskMirrorRunStatus =
  | "ok"
  | "off"
  | "busy"
  | "not-connected"
  | "failed"
  | "unknown-service";

/** The switch is saved; switching a tool on syncs it at once. */
export type SetUserTaskMirrorResp = {
  ok: true;
  outcome: { status: UserTaskMirrorRunStatus } | null;
};

/** What Sync now found; the counts are for the agent's summary. */
export type CheckTaskToolResp = { status: UserTaskMirrorRunStatus };
